// src/pos-app/surfaces/cashier/lib/pendingOrderFlow.js
//
// F12a-N Task 6 — Pending orders (hold/recall/edit/delete/settle). Faithful,
// cut-and-paste extraction from the legacy page, same discipline as Task 5's
// lib/transactionFlow.js. The legacy page (frontend/src/pages/pos/
// CashierSalesPage.js) is UNTOUCHED and keeps its own inline copies of every
// function below. Only mechanical changes were made: closure-captured React
// state became explicit parameters, bundled into a single `ctx` object where
// the legacy closure captured many variables at once (mirroring Task 5's
// `processTransactionWithPayment` ctx pattern).
//
// Source-range table (legacy CashierSalesPage.js, current line numbers):
//   deductStockForPendingOrder, revertStockDeduction, updateOrderTrackingRecords
//                                 <- NOT page-local: already shared, pure
//                                    exports of utils/OrderHelpers.js (used by
//                                    the legacy page itself, imported exactly
//                                    like this). Nothing to extract — re-
//                                    exported here so Task 6 has one import
//                                    point alongside the rest of this lib,
//                                    same precedent as Task 5's
//                                    createTransactionFromOrder re-export.
//   printPendingOrderReceipts    <- 1108-1229 (kitchen/bar tickets + order-
//                                    tracking records + cross-tab orderEvent
//                                    for a freshly-created pending order)
//   createPendingOrder           <- 932-1049  (handlePendingOrderCreation —
//                                    the HOLD flow: collision guard -> stock
//                                    deduct -> tenantStorage.saveOrder ->
//                                    printPendingOrderReceipts -> markFirstOrder
//                                    -> reset, with stock-revert rollback on
//                                    failure)
//   loadOrderForEdit              <- components/pos/modals/TableOrderModal.js
//                                    112-134 (its inline onEdit handler) — NOT
//                                    page-local, but tightly-coupled business
//                                    logic worth its own faithful extraction
//                                    (see OrdersPanelNative's header comment
//                                    for why TableOrderModal itself isn't
//                                    reused wholesale).
//   printUpdatedOrderReceipts    <- 1580-1666 (diffs original vs updated
//                                    products, prints only NEW/increased
//                                    quantities — legacy quirk preserved
//                                    verbatim: unlike printPendingOrderReceipts,
//                                    this one does NOT gate on `printingEnabled`
//                                    and passes `tenantConfig` directly as the
//                                    generateReceiptHTML `tenantInfo` arg
//                                    instead of building a `tenantInfo` shape —
//                                    both are legacy inconsistencies, kept
//                                    byte-faithful, not "fixed")
//   saveEditedOrder               <- 1500-1577 (UPDATE ORDER path: builds the
//                                    updated order, tenantStorage.saveOrder,
//                                    printUpdatedOrderReceipts,
//                                    updateOrderTrackingRecords, then the
//                                    `finally` block that always clears
//                                    editing/form state even on error)
//   deletePendingOrderAction     <- 1668-1732 (handleDeleteOrder's `onDelete`
//                                    callback body, i.e. everything AFTER
//                                    useDeletionConfirm.confirmDeletion's
//                                    reason capture — the modal/reason-capture
//                                    itself stays a UI concern in the
//                                    component, exactly like legacy's
//                                    `deletionConfirm` hook usage)
//   prepareOrderForPayment        <- 3239-3279 ("Confirm Payment for Pending
//                                    Order Modal"'s per-method button
//                                    onClick — recalculates the service
//                                    charge, loads the order into the shared
//                                    builder/dining state exactly like Edit
//                                    does, then builds the transaction via
//                                    createTransactionFromOrder so the
//                                    Task-5 PaymentConfirmModal /
//                                    processTransactionWithPayment can take
//                                    over unmodified — currentConfirmOrder
//                                    already has a full pending-order branch
//                                    there, nothing to change in Task 5's lib)
//   printOrderPreview             <- components/pos/PendingOrderCard.js:39-90
//                                    (handlePrint — PREVIEW-ONLY receipt with
//                                    the red dashed banner, markOrderPrinted,
//                                    and — when an occupation exists —
//                                    best-effort requestBill, i.e. "Print" in
//                                    the order detail doubles as "Request
//                                    Bill" exactly like legacy's PendingOrderCard)
//   changeOrderTable              <- 3349-3386 (ChangeTableModal's onConfirm
//                                    callback body — collision guard,
//                                    tenantStorage.changeOrderTable, moves
//                                    the pendingOrders/pendingOrderIds entry
//                                    from the old key to the new one)
//
// Every setter/getter the legacy closures captured is passed explicitly via
// a `ctx` object (see each function's destructuring block) instead of being
// read off component state — the only structural change from the legacy
// source; the logic, branching, and step ordering are unchanged.
import { format } from 'date-fns';
import { orderTrackingService, occupationService, transactionService } from '../../../../services/api';
import tenantStorage from '../../../../services/tenantStorage';
import { generateReceiptHTML, printWithIframe } from '../../../../utils/PrintingUtils';
import { startTicketPrint } from '../../../../utils/ticketPrintDispatch';
import { createTransactionFromOrder } from '../../../../utils/OrderManagement';
import { localPrintWouldBeSilentlyDropped } from '../../../../utils/printRouting';
import host from '../../../../host';
import {
    deductStockForPendingOrder,
    revertStockDeduction,
    deductStockForAddedItems,
    updateOrderTrackingRecords,
} from '../../../../utils/OrderHelpers';

// Paid-order lifecycle lives in utils/ so the LEGACY till can import it
// without reaching across into pos-app/. Re-exported here so this surface
// keeps a single import point, exactly like transactionFlow.js does for
// createItemsWithAddons.
export {
    createPaidOrder,
    clearPaidOrder,
    deletePaidOrderAction,
} from '../../../../utils/paidOrders';

// Task N round 3 (user-QC finding 3) — shared by printPendingOrderReceipts /
// printUpdatedOrderReceipts / printOrderPreview below. Native-only: legacy's
// own inline copies of these steps (CashierSalesPage.js, see each function's
// header for its exact source range) are untouched. Mirrors PrintingUtils'
// own decision input (utils/printRouting.js's shouldPrintAtAll) so a "print"
// call that would otherwise silently reach
// PrintingUtils' hidden-iframe fallback — a no-op inside Capacitor's iOS
// WKWebView, see transactionFlow.js's printSaleReceipts header for the full
// root-cause writeup — is skipped up front and surfaced as a toast instead.
const printReceiptToast = (t) => (t
    ? t('receipt_printing_requires_network', {}, 'Receipt printing on this device requires network printing — enable it in Portal Settings.')
    : 'Receipt printing on this device requires network printing — enable it in Portal Settings.');

export {
    deductStockForPendingOrder,
    revertStockDeduction,
    deductStockForAddedItems,
    updateOrderTrackingRecords,
};

// ---------------------------------------------------------------------------
// printPendingOrderReceipts — legacy CashierSalesPage.js:1108-1229 (verbatim).
// ---------------------------------------------------------------------------
export const printPendingOrderReceipts = async (order, ctx) => {
    const {
        setSuccess, setError, t,
        brandName, currency, printableCategories, tenantConfig,
        cashierName, selectedPaymentMethod, selectedDiningOption, tableNumber, notes,
        createReceiptTranslator, receiptTranslationLanguage,
        receiptTranslationsEnabled, dynamicTranslations, translationsLoaded,
        printingEnabled,
    } = ctx;

    try {
        setSuccess('Preparing order for kitchen/bar...');

        const tenantInfo = {
            brand_name: brandName,
            currency,
            printable_categories: printableCategories,
            receipt_header: tenantConfig?.receipt_header,
            receipt_footer: tenantConfig?.receipt_footer,
            receipt_logo: tenantConfig?.receipt_logo ? (tenantConfig.receipt_logo.startsWith('http') ? tenantConfig.receipt_logo : `${window.location.origin}/${tenantConfig.receipt_logo}`) : null,
        };

        const receipts = [];
        const trackingRecords = [];
        // Task N round 3 finding 3 — set below only if the "OTHER ITEMS"
        // print attempt is actually skipped by the capacitor gate, so the
        // trailing success message doesn't falsely claim "receipts printed".
        let printSkipped = false;

        for (const category of printableCategories) {
            const categoryItems = order.products.filter((p) => p.category === category);
            if (categoryItems.length > 0) {
                const categoryData = {
                    date: format(new Date(), 'yyyy-MM-dd'),
                    time: format(new Date(), 'HH:mm:ss'),
                    cashier: cashierName,
                    paymentMethod: selectedPaymentMethod,
                    diningOption: selectedDiningOption,
                    tableNumber: tableNumber,
                    products: categoryItems,
                    notes: notes,
                };
                const receiptT = createReceiptTranslator(receiptTranslationLanguage);

                receipts.push({
                    title: `${category.toUpperCase()} ORDER`,
                    html: generateReceiptHTML(categoryData, `${category.toUpperCase()} ORDER`, true, tenantInfo, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
                });

                try {
                    const trackingData = {
                        transaction_id: null,
                        order_id: order.order_id,
                        category: category,
                        printed_categories: true,
                        items: categoryItems.map((item) => ({
                            name: item.name,
                            quantity: item.quantity,
                            notes: item.notes || null,
                            add_ons: item.add_ons || [],
                        })),
                        table_info: {
                            dining_option: selectedDiningOption,
                            table_number: tableNumber,
                            staff_name: cashierName || 'Unknown',
                        },
                        total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                        notes: notes || null,
                    };
                    const trackingResult = await orderTrackingService.createOrderTracking(trackingData);
                    trackingRecords.push(trackingResult);

                    // The kitchen gets paper, not just a screen.
                    //
                    // This path created the tracking record and stopped, so a
                    // dine-in order sent to the kitchen showed on the KDS and
                    // produced no print job at all — confirmed in QC on
                    // 2026-09-08 (TRK625445DF: record created, zero jobs).
                    // Every other path that creates a record prints beside it:
                    // transactionFlow's immediate sale, drawerFlow's waiter
                    // submit, OrderHelpers' edit.
                    //
                    // It matters most here. A sent order is paid much later,
                    // and dispatchKitchenTickets deliberately does NOT print
                    // for an already-sent order at payment time — it only
                    // attaches the transaction_id — so there is no second
                    // chance and, equally, no risk of a duplicate docket.
                    //
                    // Started, never awaited, and never allowed to throw: the
                    // order is already with the kitchen on screen, so losing
                    // the docket is bad but failing the send would be worse.
                    if (trackingResult?.tracking_id) {
                        try {
                            startTicketPrint(category, trackingResult.tracking_id, tenantConfig);
                        } catch (printError) {
                            console.error(`Ticket print failed for ${category}:`, printError);
                        }
                    }

                    const eventData = {
                        type: 'orderSubmitted',
                        category: category,
                        orderType: 'new_order',
                        orderId: order.order_id,
                        timestamp: Date.now(),
                    };
                    localStorage.setItem('orderEvent', JSON.stringify(eventData));
                    localStorage.removeItem('orderEvent');
                } catch (trackingError) {
                    console.error(`Failed to create tracking for ${category}:`, trackingError);
                }
            }
        }

        const otherItems = order.products.filter((p) => !printableCategories.includes(p.category));
        if (otherItems.length > 0) {
            const otherData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: selectedPaymentMethod,
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: otherItems,
                notes: notes,
            };
            const receiptT = createReceiptTranslator(receiptTranslationLanguage);

            receipts.push({
                title: 'OTHER ITEMS',
                html: generateReceiptHTML(otherData, 'OTHER ITEMS', true, tenantInfo, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
            });
        }

        if (receipts.length > 0 && printingEnabled) {
            // Task N round 3 finding 3 — see the printReceiptToast/
            // localPrintWouldBeSilentlyDropped header comment above this
            // file's imports.
            if (localPrintWouldBeSilentlyDropped(tenantConfig?.printing?.method, host)) {
                printSkipped = true;
                setError(printReceiptToast(t));
            } else {
                const allReceiptsHTML = receipts.map((receipt) => `<div class="print-page">${receipt.html}</div>`).join('');
                printWithIframe(allReceiptsHTML, `${brandName.toUpperCase()} Orders`, tenantConfig?.printing?.method);
            }
        }

        if (trackingRecords.length > 0) {
            setSuccess(printSkipped
                ? `Order sent to ${trackingRecords.length} station(s). Staff can now start preparing!`
                : `Order sent to ${trackingRecords.length} station(s) and receipts printed. Staff can now start preparing!`);
        }
    } catch (err) {
        console.error('Error during printing and tracking:', err);
        setError('There was an error processing the order. Please try again.');
    }
};

// ---------------------------------------------------------------------------
// createPendingOrder — legacy handlePendingOrderCreation, CashierSalesPage.js:
// 932-1049 (the HOLD flow). Never throws — every failure branch is handled
// internally (setError + rollback), matching the legacy function exactly.
// ---------------------------------------------------------------------------
export const createPendingOrder = async (ctx) => {
    const {
        t,
        selectedDiningOption, tableNumber, editingOrderKey,
        pendingOrders, setPendingOrders, setPendingOrderIds, setPrintedOrders,
        selectedProducts, cashierName, businessDaySalesDate,
        totals, discountAmount, addServiceCharge, addTax, notes,
        appliedPromo, promoDiscountAmount,
        setError, setSuccess,
        activeOccupation,
        refreshBulkStock,
        resetFormState, setSelectedCategory, setSelectedSubcategory,
        setSelectedDiningOption, setTableNumber, setTableNumberConfirmed, resetPromo,
    } = ctx;

    const tableKey = `${selectedDiningOption}-${tableNumber}`;

    // Step 1: table already in use (and not the one being edited).
    if (pendingOrders[tableKey] && editingOrderKey !== tableKey) {
        setError(t('table_already_has_pending_order', { option: selectedDiningOption, number: tableNumber }));
        return;
    }

    // Step 2: DEDUCT STOCK IMMEDIATELY when creating a pending order.
    try {
        await deductStockForPendingOrder(selectedProducts, selectedDiningOption, tableNumber, cashierName);
    } catch (error) {
        console.error('Error deducting stock for pending order:', error);
        setError(t('failed_to_deduct_stock'));
        return;
    }

    // Step 3: not printed initially.
    setPrintedOrders((prev) => {
        const updated = { ...prev };
        delete updated[tableKey];
        return updated;
    });

    // Step 4: pending order object.
    const pendingOrder = {
        time: format(new Date(), 'HH:mm:ss'),
        date: format(new Date(), 'yyyy-MM-dd'),
        sales_date: (await tenantStorage.getState('salesDate')) || businessDaySalesDate,
        cashier: cashierName,
        diningOption: selectedDiningOption,
        tableNumber: tableNumber,
        products: [...selectedProducts],
        subtotal: totals.subtotal,
        discount: discountAmount,
        serviceCharge: totals.serviceCharge,
        total: Math.max(0, totals.total - promoDiscountAmount),
        notes: notes,
        paymentMethod: '',
        addServiceCharge: addServiceCharge,
        addTax: addTax,
        tax: totals.tax,
        is_submitted: false,
        is_printed: false,
        appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
        promoDiscountAmount: promoDiscountAmount,
    };

    try {
        const currentSalesDate = await tenantStorage.getState('salesDate');
        const response = await tenantStorage.saveOrder(tableKey, pendingOrder, false, false, null, currentSalesDate);

        const orderId = response.order_id;
        if (!orderId) {
            throw new Error('Failed to get order ID from tenant storage');
        }

        // Step 5: local state.
        setPendingOrders((prev) => ({ ...prev, [tableKey]: { ...pendingOrder, order_id: orderId } }));
        setPendingOrderIds((prev) => ({ ...prev, [tableKey]: orderId }));

        // Step 6: kitchen/bar receipts + tracking.
        const orderWithId = { ...pendingOrder, order_id: orderId };
        await printPendingOrderReceipts(orderWithId, ctx);

        // Step 7: reset UI state (legacy resetAllFormState — resetFormState is
        // useOrderBuilder's shared export, same semantics).
        resetFormState();
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedDiningOption(null);
        setTableNumber('');
        setTableNumberConfirmed(false);
        resetPromo();

        setSuccess(t('order_added_to_pending_success'));
        setTimeout(() => setSuccess(''), 2000);

        if (activeOccupation?.id) {
            try { await occupationService.markFirstOrder(activeOccupation.id); } catch {}
        }

        // Step 8: refresh stock to reflect the deduction.
        await refreshBulkStock();
    } catch (error) {
        console.error('Failed to create pending order:', error);
        setError(t('failed_to_save_pending_order'));

        setPendingOrders((prev) => {
            const cleaned = { ...prev };
            delete cleaned[tableKey];
            return cleaned;
        });
        setPendingOrderIds((prev) => {
            const cleaned = { ...prev };
            delete cleaned[tableKey];
            return cleaned;
        });

        // REVERT STOCK DEDUCTION if order creation failed.
        try {
            await revertStockDeduction(selectedProducts, cashierName);
        } catch (revertError) {
            console.error('Failed to revert stock deduction:', revertError);
        }

        await refreshBulkStock();
    }
};

// ---------------------------------------------------------------------------
// loadOrderForEdit — components/pos/modals/TableOrderModal.js:112-134 (its
// inline onEdit handler). The modal's own onClose()/window.scrollTo calls are
// UI-only and stay with the caller (closing the Orders panel).
// ---------------------------------------------------------------------------
export const loadOrderForEdit = (orderKey, order, ctx) => {
    const {
        setSelectedProducts, setSelectedDiningOption, setTableNumber,
        setAddServiceCharge, setAddTax, setDiscountAmount, setNotes,
        setSelectedPaymentMethod, setEditingOrderKey, setCurrentOrderId,
        pendingOrderIds,
    } = ctx;

    const productsWithUniqueIds = order.products.map((product) => ({
        ...product,
        uniqueId: product.uniqueId || `${product.product_code}-${(product.add_ons || []).map((a) => a.id).join('-')}-existing`,
    }));

    setSelectedProducts(productsWithUniqueIds);
    setSelectedDiningOption(order.diningOption);
    setTableNumber(order.tableNumber);
    setAddServiceCharge(order.addServiceCharge);
    setAddTax(order.addTax || false);
    setDiscountAmount(order.discount);
    setNotes(order.notes);
    setSelectedPaymentMethod(order.paymentMethod);
    setEditingOrderKey(orderKey);
    setCurrentOrderId(pendingOrderIds[orderKey]);
};

// ---------------------------------------------------------------------------
// printUpdatedOrderReceipts — legacy CashierSalesPage.js:1580-1666 (verbatim,
// INCLUDING its two quirks vs printPendingOrderReceipts: no `printingEnabled`
// gate, and `tenantConfig` passed directly as generateReceiptHTML's
// `tenantInfo` arg rather than a constructed tenantInfo shape).
// ---------------------------------------------------------------------------
export const printUpdatedOrderReceipts = (originalOrder, updatedOrder, ctx) => {
    const {
        brandName, printableCategories, tenantConfig,
        createReceiptTranslator, receiptTranslationLanguage,
        receiptTranslationsEnabled, dynamicTranslations, translationsLoaded,
        t, setError,
    } = ctx;

    const originalProducts = originalOrder.products || [];
    const updatedProducts = updatedOrder.products || [];
    const newProducts = [];

    updatedProducts.forEach((updatedProduct) => {
        const originalProduct = originalProducts.find((p) => {
            if (p.product_code !== updatedProduct.product_code) return false;
            const originalAddons = (p.add_ons || []).map((addon) => addon.product_code || addon.id).sort();
            const updatedAddons = (updatedProduct.add_ons || []).map((addon) => addon.product_code || addon.id).sort();
            if (originalAddons.length !== updatedAddons.length) return false;
            return originalAddons.every((id, index) => id === updatedAddons[index]);
        });

        if (!originalProduct) {
            newProducts.push({ ...updatedProduct });
        } else if (updatedProduct.quantity > originalProduct.quantity) {
            const additionalQuantity = updatedProduct.quantity - originalProduct.quantity;
            newProducts.push({ ...updatedProduct, quantity: additionalQuantity });
        }
    });

    if (newProducts.length === 0) {
        return;
    }

    const receiptData = {
        date: updatedOrder.date,
        time: format(new Date(), 'HH:mm:ss'),
        cashier: updatedOrder.cashier,
        paymentMethod: updatedOrder.paymentMethod,
        diningOption: updatedOrder.diningOption,
        tableNumber: updatedOrder.tableNumber,
        products: newProducts,
        notes: updatedOrder.notes,
    };

    const receipts = [];

    printableCategories.forEach((category) => {
        const categoryItems = newProducts.filter((p) => p.category === category);
        if (categoryItems.length > 0) {
            const categoryData = { ...receiptData, products: categoryItems };
            const receiptT = createReceiptTranslator(receiptTranslationLanguage);
            receipts.push({
                html: generateReceiptHTML(categoryData, `${category.toUpperCase()} ORDER - UPDATED`, true, tenantConfig, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
                title: `${category.toUpperCase()} ORDER - UPDATED`,
            });
        }
    });

    const otherItems = newProducts.filter((p) => !printableCategories.includes(p.category));
    if (otherItems.length > 0) {
        const otherData = { ...receiptData, products: otherItems };
        const receiptT = createReceiptTranslator(receiptTranslationLanguage);
        receipts.push({
            html: generateReceiptHTML(otherData, 'OTHER ITEMS - UPDATED', true, tenantConfig, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
            title: 'OTHER ITEMS - UPDATED',
        });
    }

    if (receipts.length === 0) {
        return;
    }

    // Task N round 3 finding 3 — see the printReceiptToast/
    // localPrintWouldBeSilentlyDropped header comment above this file's
    // imports.
    if (localPrintWouldBeSilentlyDropped(tenantConfig?.printing?.method, host)) {
        if (typeof setError === 'function') setError(printReceiptToast(t));
        return;
    }

    const allReceiptsHTML = receipts.map((receipt) => `<div class="print-page">${receipt.html}</div>`).join('');
    printWithIframe(allReceiptsHTML, `${brandName.toUpperCase()} Updated Orders`, tenantConfig?.printing?.method);
};

// ---------------------------------------------------------------------------
// saveEditedOrder — legacy CashierSalesPage.js:1500-1577 (UPDATE ORDER path).
// The `finally` block always clears editing/form state, even on error,
// exactly like legacy.
// ---------------------------------------------------------------------------
export const saveEditedOrder = async (ctx) => {
    const {
        editingOrderKey, pendingOrders, pendingOrderIds,
        cashierName, selectedDiningOption, tableNumber, selectedProducts, notes,
        selectedPaymentMethod, addServiceCharge, addTax, totals,
        appliedPromo, promoDiscountAmount, discountAmount,
        setPendingOrders, setSuccess, setError, t,
        username, products, tenantConfig, printableCategories,
        resetFormState, setEditingOrderKey, setSelectedCategory, setSelectedSubcategory,
        setTableNumber, setTableNumberConfirmed, setSelectedDiningOption,
        setAddServiceCharge, setAddTax, setDiscountMode,
    } = ctx;

    if (!editingOrderKey) return;

    try {
        const originalOrder = { ...pendingOrders[editingOrderKey] };

        const updatedOrder = {
            time: pendingOrders[editingOrderKey].time,
            date: format(new Date(), 'yyyy-MM-dd'),
            cashier: cashierName,
            diningOption: selectedDiningOption,
            tableNumber: tableNumber,
            products: [...selectedProducts],
            subtotal: totals.subtotal,
            discount: discountAmount,
            serviceCharge: totals.serviceCharge,
            total: Math.max(0, totals.total - promoDiscountAmount),
            notes: notes,
            paymentMethod: selectedPaymentMethod,
            addServiceCharge: addServiceCharge,
            addTax: addTax,
            tax: totals.tax,
            appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
            promoDiscountAmount: promoDiscountAmount,
        };

        const currentSalesDate = await tenantStorage.getState('salesDate');
        await tenantStorage.saveOrder(editingOrderKey, updatedOrder, false, false, null, currentSalesDate);
        setPendingOrders((prev) => ({ ...prev, [editingOrderKey]: updatedOrder }));

        // Anything this edit ADDED has to come off the shelf now.
        //
        // The order reserved its stock when it was held, and a settled
        // pending order now tells the server that stock is already gone
        // (TransactionCreate.stock_already_deducted) — so nothing downstream
        // will deduct the extra items. Before that flag existed, the
        // settle-time decrement covered for this by taking the WHOLE bill
        // off again, which over-deducted the original items in exchange.
        //
        // Isolated: the order is saved and the kitchen already updated, so
        // failing the edit over a stock figure would be strictly worse than
        // a figure that needs correcting.
        try {
            await deductStockForAddedItems(
                originalOrder.products || [], selectedProducts,
                selectedDiningOption, tableNumber, cashierName,
            );
        } catch (stockError) {
            console.error('Failed to deduct stock for added items:', stockError);
        }

        printUpdatedOrderReceipts(originalOrder, updatedOrder, ctx);

        const orderId = pendingOrderIds[editingOrderKey];
        if (orderId) {
            await updateOrderTrackingRecords(
                originalOrder.products || [],
                selectedProducts,
                orderId,
                originalOrder,
                tenantConfig,
                selectedDiningOption,
                editingOrderKey,
                username,
                products,
                true,
                { printableCategories }
            );
        }

        setSuccess(t('order_updated_successfully'));
        setTimeout(() => setSuccess(''), 1000);
    } catch (err) {
        console.error('Error saving edited order:', err);
        setError(t('failed_to_update_order', { error: err.message }));
    } finally {
        resetFormState();
        setEditingOrderKey(null);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setTableNumber('');
        setTableNumberConfirmed(false);
        setSelectedDiningOption(null);
        setAddServiceCharge(false);
        setAddTax(false);
        setDiscountMode('direct');
    }
};

// ---------------------------------------------------------------------------
// deletePendingOrderAction — legacy CashierSalesPage.js:1668-1732, the
// `deletionConfirm.confirmDeletion({ onDelete: ... })` callback body. The
// reason-capture UI itself (useDeletionConfirm + DeletionConfirmModal) stays
// in the component, exactly like legacy. Rethrows on error so the modal
// (loading/error state) knows the deletion failed, byte-faithful.
// ---------------------------------------------------------------------------
export const deletePendingOrderAction = async (orderKey, reason, ctx) => {
    const {
        order, cashierName,
        setPendingOrders, setPrintedOrders, setPendingOrderIds,
        setSuccess, setError, setLoading, t,
    } = ctx;

    try {
        setLoading(true);

        try {
            await orderTrackingService.deletePendingOrder(orderKey, reason, order, cashierName);
        } catch (backendError) {
            console.error('Failed to log order deletion in backend:', backendError);
            if (backendError.response?.status === 403) {
                setError(t('insufficient_permissions_delete_order'));
                setLoading(false);
                throw backendError;
            }
            // Continue with frontend deletion even if backend logging fails.
        }

        await tenantStorage.deleteOrder(orderKey);

        setPendingOrders((prev) => {
            const newOrders = { ...prev };
            delete newOrders[orderKey];
            return newOrders;
        });
        setPrintedOrders((prev) => {
            const newPrintedOrders = { ...prev };
            delete newPrintedOrders[orderKey];
            return newPrintedOrders;
        });
        setPendingOrderIds((prev) => {
            const newOrderIds = { ...prev };
            delete newOrderIds[orderKey];
            return newOrderIds;
        });

        setSuccess(t('order_deleted_successfully'));
        setLoading(false);
        setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
        setLoading(false);
        if (err.response?.status === 403) {
            setError(t('insufficient_permissions_delete_order'));
        } else {
            setError(t('failed_to_delete_order'));
        }
        console.error('Error deleting order:', err);
        throw err;
    }
};

// ---------------------------------------------------------------------------
// prepareOrderForPayment — legacy CashierSalesPage.js:3239-3279 (the
// "Confirm Payment for Pending Order Modal"'s per-method button onClick).
// Loads the order into the shared builder/dining state exactly like Edit
// does (minus editingOrderKey), then builds the transaction. The caller sets
// setPendingTransaction/setCurrentConfirmOrder/setPaymentConfirmModalOpen —
// that is legacy's own next line at the same call site (3273-3274) and stays
// UI glue, same convention as Task 5's buildTransaction/handleCharge split.
// ---------------------------------------------------------------------------
export const prepareOrderForPayment = async (orderKey, method, ctx) => {
    const {
        t, pendingOrders, recalculateOrderWithServiceCharge, setError,
        setSelectedProducts, setSelectedPaymentMethod, setSelectedDiningOption,
        setTableNumber, setAddServiceCharge, setAddTax, setDiscountAmount, setNotes,
    } = ctx;

    const order = pendingOrders[orderKey];
    if (!order) {
        // Task 8 item 3 — was the raw untranslated string 'order_not_found';
        // t() the same registered key (verified present in depos_dev) so a
        // recall-race (order deleted/settled elsewhere between opening the
        // panel and tapping a payment method) shows real copy, not a key.
        setError(t ? t('order_not_found') : 'order_not_found');
        return null;
    }

    try {
        const updatedOrder = recalculateOrderWithServiceCharge(order);
        updatedOrder.paymentMethod = method;

        setSelectedProducts(updatedOrder.products);
        setSelectedPaymentMethod(method);
        setSelectedDiningOption(updatedOrder.diningOption);
        setTableNumber(updatedOrder.tableNumber);
        setAddServiceCharge(updatedOrder.addServiceCharge);
        setAddTax(updatedOrder.addTax || false);
        setDiscountAmount(updatedOrder.discount);
        setNotes(updatedOrder.notes);

        const currentSalesDate = await tenantStorage.getState('salesDate');
        if (!currentSalesDate) {
            throw new Error('Sales date not found');
        }

        const transaction = createTransactionFromOrder(updatedOrder, new Date(), currentSalesDate);
        transaction.sales_date = currentSalesDate;
        transaction.payment_method = method;
        transaction.products_json = JSON.stringify(updatedOrder.products);

        return transaction;
    } catch (error) {
        console.error('Error preparing transaction:', error);
        setError(`Failed to prepare transaction: ${error.message}`);
        return null;
    }
};

// ---------------------------------------------------------------------------
// printOrderPreview — components/pos/PendingOrderCard.js:39-90 (handlePrint).
// PREVIEW-ONLY receipt (red dashed "not paid" banner top+bottom), then marks
// the order printed and — when an occupation exists for the table — fires a
// best-effort requestBill (legacy: "Print" doubles as "Request Bill" from
// the order card). `occupation` is optional; failures there are swallowed
// exactly like legacy's bare `catch {}`.
// ---------------------------------------------------------------------------
export const printOrderPreview = async (orderKey, order, ctx) => {
    const {
        pendingOrderIds, setPrintedOrders, currency, tenantConfig, brandName, t,
        printableCategories, occupation, setSuccess, setError,
    } = ctx;
    if (!order) return;

    const receiptData = {
        date: order.date,
        time: order.time,
        cashier: order.cashier,
        paymentMethod: order.paymentMethod,
        diningOption: order.diningOption,
        tableNumber: order.tableNumber,
        products: order.products,
        subtotal: order.subtotal,
        discount: order.discount,
        serviceCharge: order.serviceCharge,
        total: order.total,
        notes: order.notes || t('preview_not_finalized'),
    };

    const tenantInfo = {
        brand_name: brandName,
        currency,
        printable_categories: printableCategories || [],
        receipt_header: tenantConfig?.receipt_header,
        receipt_footer: tenantConfig?.receipt_footer,
        receipt_logo: tenantConfig?.receipt_logo,
    };

    const receiptHTML = generateReceiptHTML(
        { ...receiptData, notes: receiptData.notes + ` (${t('preview_only_not_paid')})` },
        t('receipt_preview').toUpperCase(),
        false,
        tenantInfo
    );

    const previewHTML = `
        <div class="print-page">
            <div class="preview-banner" style="background-color: #fef2f2; color: #b91c1c; text-align: center; padding: 4px 0; font-weight: bold; margin: 8px 0; border: 1px dashed #b91c1c;">
                ${t('preview_only_not_paid')}
            </div>
            ${receiptHTML}
            <div class="preview-banner" style="background-color: #fef2f2; color: #b91c1c; text-align: center; padding: 4px 0; font-weight: bold; margin: 8px 0; border: 1px dashed #b91c1c;">
                ${t('preview_only_not_paid')}
            </div>
        </div>
    `;

    // Task N round 3 finding 3 — see the printReceiptToast/
    // localPrintWouldBeSilentlyDropped header comment above this file's
    // imports. markOrderPrinted/setPrintedOrders/requestBill below are
    // deliberately NOT gated on this — legacy already fires them
    // fire-and-forget regardless of whether the (never-awaited) print call
    // actually produced paper (e.g. no default printer in Electron), and
    // "Print" doubling as "Request Bill" is a real workflow action the
    // cashier needs even when this device can't physically print.
    const printBlocked = localPrintWouldBeSilentlyDropped(tenantConfig?.printing?.method, host);
    if (printBlocked) {
        if (typeof setError === 'function') setError(printReceiptToast(t));
    } else {
        printWithIframe(previewHTML, `${brandName.toUpperCase()} ${t('receipt_preview')}`, tenantConfig?.printing?.method, tenantConfig);
    }

    const orderId = pendingOrderIds[orderKey];
    await tenantStorage.markOrderPrinted(orderKey, true, orderId);
    setPrintedOrders((prev) => ({ ...prev, [orderKey]: true }));

    // Task 8 item 4 — legacy PendingOrderCard.js:85-86 (`await onPrint(...)`
    // then `setSuccess(t('preview_receipt_printed'))`, auto-cleared after
    // 1s) was dropped in the Task 6 extraction; restored here so the
    // cashier gets the same confirmation toast after a preview print.
    // Task N round 3 finding 3 — suppressed when the print itself was
    // blocked above (the toast setError already fired instead — showing
    // BOTH "printed" success and "printing unavailable" error would
    // contradict each other).
    if (setSuccess && !printBlocked) {
        setSuccess(t('preview_receipt_printed'));
        setTimeout(() => setSuccess(''), 1000);
    }

    if (occupation) {
        try { await occupationService.requestBill(occupation.id); } catch {}
    }
};

// ---------------------------------------------------------------------------
// changeOrderTable — legacy CashierSalesPage.js:3349-3386 (ChangeTableModal's
// onConfirm callback body).
// ---------------------------------------------------------------------------
export const changeOrderTable = async (orderKey, newDiningOption, newTableNumber, ctx) => {
    const {
        pendingOrders, pendingOrderIds, setPendingOrders, setPendingOrderIds,
        setChangeTableModalOpen, setSuccess, setError, t,
    } = ctx;

    if (!orderKey || !newTableNumber || !newDiningOption) return;

    try {
        const originalOrder = pendingOrders[orderKey];
        const newTableKey = `${newDiningOption}-${newTableNumber}`;

        if (Object.keys(pendingOrders).includes(newTableKey)) {
            setError(`${newDiningOption} ${newTableNumber} already has a pending order`);
            return;
        }

        const orderId = pendingOrderIds[orderKey];
        await tenantStorage.changeOrderTable(orderId, newTableKey, newDiningOption, newTableNumber);

        const updatedOrder = { ...originalOrder, tableNumber: newTableNumber, diningOption: newDiningOption };

        setPendingOrders((prev) => {
            const newOrders = { ...prev };
            delete newOrders[orderKey];
            newOrders[newTableKey] = updatedOrder;
            return newOrders;
        });
        setPendingOrderIds((prev) => {
            const newOrderIds = { ...prev };
            delete newOrderIds[orderKey];
            newOrderIds[newTableKey] = orderId;
            return newOrderIds;
        });

        setChangeTableModalOpen(false);
        setSuccess(t('order_moved_to', { diningOption: newDiningOption, tableNumber: newTableNumber }));
        setTimeout(() => setSuccess(''), 1000);
    } catch (err) {
        setError(t('failed_to_change_table'));
    }
};
